import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, GoogleLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PasswordStrength, evaluatePassword } from "@/components/PasswordStrength";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

const LOGIN_BG =
  "https://images.unsplash.com/photo-1554469384-e58fac16e23a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const DEMO = [
  { email: "cadmin@ncgov.gov", label: "Contract Administrator" },
  { email: "gmanager@ncgov.gov", label: "Grant Manager" },
  { email: "cspecialist@ncgov.gov", label: "Contract Specialist" },
  { email: "legal@ncgov.gov", label: "Legal Review" },
  { email: "leadership@ncgov.gov", label: "Executive Leadership" },
];

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [orgName, setOrgName] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (mode) => {
    if (mode === "register" && !evaluatePassword(password).valid) {
      return toast.error("Please choose a stronger password (8+ chars, mix of upper, lower, number, symbol)");
    }
    setBusy(true);
    try {
      const path = mode === "register" ? "/auth/register" : "/auth/login";
      const body = mode === "register" ? { email, password, name, org_name: orgName } : { email, password };
      const { data } = await api.post(path, body);
      setUser(data);
      toast.success(`Welcome, ${data.name}`);
      navigate("/dashboard", { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const quick = (e) => {
    setEmail(e);
    setPassword("Admin@123");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left brand panel */}
      <div className="relative hidden lg:block">
        <img src={LOGIN_BG} alt="grantSME workspace" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/80" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2">
            <ShieldCheck size={30} weight="fill" className="text-gold" />
            <span className="font-head font-bold text-lg">grantSME</span>
          </div>
          <div>
            <div className="overline text-gold mb-3">Contract &amp; Grant Management</div>
            <h1 className="font-head font-bold text-4xl xl:text-5xl leading-none tracking-tight max-w-lg">
              Manage the full lifecycle of contracts and grant-funded work.
            </h1>
            <p className="text-white/75 mt-5 max-w-md leading-relaxed">
              Structured intake, sequential role-based review, document control, and reporting — one secure
              workspace for your whole organization.
            </p>
          </div>
          <div className="text-white/50 text-xs">grantSME · Multi-organization workspace</div>
        </div>
      </div>

      {/* Right auth panel */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md rise">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <ShieldCheck size={28} weight="fill" className="text-primary" />
            <span className="font-head font-bold text-lg">grantSME</span>
          </div>

          <h2 className="font-head font-bold text-2xl tracking-tight">Sign in to your workspace</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-6">Access is scoped to your organization and role.</p>

          <Tabs defaultValue={new URLSearchParams(window.location.search).get("mode") === "register" ? "register" : "login"}>
            <TabsList className="grid grid-cols-2 w-full mb-5">
              <TabsTrigger value="login" data-testid="tab-login">Sign In</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Register</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="space-y-4">
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
              </div>
              <div className="space-y-1.5">
                <Label>Password</Label>
                <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
              </div>
              <Button data-testid="login-submit" className="w-full" disabled={busy} onClick={() => submit("login")}>
                {busy ? "Signing in…" : "Sign In"}
              </Button>
            </TabsContent>

            <TabsContent value="register" className="space-y-4">
              <p className="text-xs text-muted-foreground -mt-1">
                Create your organization's workspace. You'll be its first administrator.
              </p>
              <div className="space-y-1.5">
                <Label>Organization name</Label>
                <Input data-testid="register-org" value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="Acme Grants Inc." />
              </div>
              <div className="space-y-1.5">
                <Label>Full name</Label>
                <Input data-testid="register-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" />
              </div>
              <div className="space-y-1.5">
                <Label>Work email</Label>
                <Input data-testid="register-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
              </div>
              <div className="space-y-1.5">
                <Label>Password</Label>
                <Input data-testid="register-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
                <PasswordStrength value={password} />
              </div>
              <Button data-testid="register-submit" className="w-full" disabled={busy} onClick={() => submit("register")}>
                {busy ? "Creating…" : "Create organization"}
              </Button>
            </TabsContent>
          </Tabs>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center"><span className="bg-background px-3 text-xs text-muted-foreground">or</span></div>
          </div>

          <Button data-testid="google-login" variant="outline" className="w-full" onClick={google}>
            <GoogleLogo size={18} weight="bold" className="mr-2" /> Continue with Google
          </Button>

          <div className="mt-8 border border-border rounded-md p-4 bg-card">
            <div className="overline text-muted-foreground mb-2">Demo accounts · password Admin@123</div>
            <div className="flex flex-wrap gap-2">
              {DEMO.map((d) => (
                <button
                  key={d.email}
                  data-testid={`demo-${d.email.split("@")[0]}`}
                  onClick={() => quick(d.email)}
                  className="text-xs px-2.5 py-1 rounded-sm border border-border hover:border-primary hover:text-primary transition-colors"
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
